import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaSpinner, FaCheckCircle, FaTimesCircle, FaCreditCard, FaLock } from 'react-icons/fa';
import io from 'socket.io-client';
import useLoadTunaSDK from '../../hooks/useLoadTunaSDK';
import {
  getTunaSession,
  createCardPaymentPublic,
  getPaymentStatusPublic
} from '../../services/api';
import getApiUrl from '../../utils/getApiUrl';
import { formatPriceWithSymbol } from '../../utils/currency';

// ✅ Função para capturar IP do cliente (para antifraude)
const getClientIP = async () => {
  try {
    // Usar ipify.org - API gratuita e rápida
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    console.log('[CARD-MODAL] IP do cliente capturado:', data.ip);
    return data.ip;
  } catch (error) {
    console.warn('[CARD-MODAL] Não foi possível capturar IP:', error.message);
    return null;
  }
};

// ✅ Função para capturar dados do navegador (para antifraude/3DS)
// sessionID é usado pelo CyberSource para correlacionar fingerprint do dispositivo
const getFrontData = async (sessionID = null) => {
  const clientIP = await getClientIP();

  return {
    sessionID: sessionID, // ✅ CRÍTICO: SessionID para CyberSource antifraude
    ipAddress: clientIP,
    userAgent: navigator.userAgent || '',
    acceptHeader: 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
    acceptLanguage: navigator.language || 'pt-BR',
    cookiesAccepted: navigator.cookieEnabled || true,
    colorDepth: String(window.screen?.colorDepth || 24),
    screenHeight: String(window.screen?.height || 768),
    screenWidth: String(window.screen?.width || 1024),
    timeDifference: String(new Date().getTimezoneOffset()),
    javaEnabled: false,
    javaScriptEnabled: true
  };
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 450px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  box-sizing: border-box;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  z-index: 1;

  &:hover {
    color: #333;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  overflow: hidden;
  box-sizing: border-box;
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;

  svg {
    font-size: 28px;
    color: white;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 24px;
  text-align: center;
`;

const PaymentAmount = styled.div`
  background: #f8f9fa;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  text-align: center;

  span {
    display: block;
    font-size: 14px;
    color: #666;
    margin-bottom: 4px;
  }

  strong {
    font-size: 28px;
    color: #1976d2;
    font-weight: 700;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }
`;

const Input = styled.input`
  padding: 14px 16px;
  border: 1px solid ${({ $error }) => ($error ? '#f44336' : '#ddd')};
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.2s;
  background: #fafafa;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ $error }) => ($error ? '#f44336' : '#1976d2')};
    background: white;
    box-shadow: 0 0 0 3px ${({ $error }) => ($error ? 'rgba(244, 67, 54, 0.1)' : 'rgba(25, 118, 210, 0.1)')};
  }

  &::placeholder {
    color: #aaa;
  }

  &:disabled {
    background: #f0f0f0;
    cursor: not-allowed;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;

  & > * {
    min-width: 0;
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #f44336;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ValidationIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  display: flex;
  align-items: center;

  &.valid {
    color: #4caf50;
  }

  &.invalid {
    color: #f44336;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 18px;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: #e8f5e9;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 12px;
  color: #2e7d32;

  svg {
    font-size: 14px;
  }
`;

const StatusOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;

  svg {
    font-size: 64px;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px;
  }

  p {
    color: #666;
    margin: 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;

  svg {
    font-size: 40px;
    color: #1976d2;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ModalPaymentCard = ({
  isOpen,
  onClose,
  amount,
  pedidoId,
  customer,
  idEmpresa,
  onPaymentSuccess,
  onPaymentFailed,
  onTransactionCreated // ✅ Callback para informar ao pai sobre a transação criada
}) => {
  const { t } = useTranslation('cardapio');

  // Estados do formulário
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardHolderDocument, setCardHolderDocument] = useState(''); // ✅ CPF/CNPJ do titular do cartão
  const [cardHolderEmail, setCardHolderEmail] = useState(''); // ✅ Email do titular (importante para antifraude CyberSource)
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [errors, setErrors] = useState({});
  const [cardValid, setCardValid] = useState(null); // null = não verificado, true = válido, false = inválido

  // Estados do pagamento
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [transactionId, setTransactionId] = useState(null);

  // Estados do Tuna SDK
  const [sessionId, setSessionId] = useState(null);
  const [sdkError, setSdkError] = useState(null);

  // Hook do SDK (com antifraudSessionId para CyberSource)
  const { tunaInstance, isLoading: sdkLoading, error: sdkLoadError, isReady: sdkReady, antifraudSessionId } = useLoadTunaSDK({
    enabled: isOpen && !!sessionId,
    sessionId,
    sandbox: process.env.REACT_APP_TUNA_SANDBOX === 'true',
    customerId: pedidoId // ✅ ID do pedido para identificar o cliente no antifraude
  });

  // 🔥 NOVO: Estado para controlar se Device Fingerprint está pronto
  const [deviceFingerprintReady, setDeviceFingerprintReady] = useState(false);
  const dfTimerRef = useRef(null);

  // 🔥 Aguardar Device Fingerprint ser coletado (mínimo 3 segundos após SDK pronto)
  useEffect(() => {
    if (sdkReady && antifraudSessionId) {
      console.log('[CARD-MODAL] 🛡️ Aguardando coleta do Device Fingerprint...');
      dfTimerRef.current = setTimeout(() => {
        setDeviceFingerprintReady(true);
        console.log('[CARD-MODAL] ✅ Device Fingerprint pronto para pagamento');
      }, 3000); // 3 segundos para CyberSource coletar dados
    } else {
      setDeviceFingerprintReady(false);
    }

    return () => {
      if (dfTimerRef.current) {
        clearTimeout(dfTimerRef.current);
      }
    };
  }, [sdkReady, antifraudSessionId]);

  const socketRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const hasInitializedRef = useRef(false); // ✅ Controle para evitar múltiplas inicializações

  // ✅ Refs para callbacks (evitar stale closures no WebSocket)
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  const onPaymentFailedRef = useRef(onPaymentFailed);
  const onCloseRef = useRef(onClose);

  // Atualizar refs quando callbacks mudarem
  useEffect(() => {
    onPaymentSuccessRef.current = onPaymentSuccess;
    onPaymentFailedRef.current = onPaymentFailed;
    onCloseRef.current = onClose;
  }, [onPaymentSuccess, onPaymentFailed, onClose]);

  // ✅ Chave para armazenar transação no localStorage (por pedido)
  const getStorageKey = () => `card_transaction_${pedidoId}`;

  // ✅ Recuperar transação existente do localStorage
  const getStoredTransaction = () => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        const data = JSON.parse(stored);
        // Verificar se transação ainda está pendente (não expirou - 30 min)
        const createdAt = new Date(data.createdAt);
        const now = new Date();
        const diffMinutes = (now - createdAt) / (1000 * 60);
        if (diffMinutes < 30 && data.status === 'pending') {
          console.log('[CARD-MODAL] ♻️ Transação existente encontrada no localStorage:', data.transactionId);
          return data;
        } else {
          console.log('[CARD-MODAL] ⏰ Transação expirada ou concluída, removendo do localStorage');
          localStorage.removeItem(getStorageKey());
        }
      }
    } catch (error) {
      console.error('[CARD-MODAL] Erro ao recuperar transação do localStorage:', error);
    }
    return null;
  };

  // ✅ Salvar transação no localStorage
  const saveTransaction = (txId, status = 'pending') => {
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify({
        transactionId: txId,
        status,
        amount,
        pedidoId,
        createdAt: new Date().toISOString()
      }));
      console.log('[CARD-MODAL] 💾 Transação salva no localStorage:', txId);
    } catch (error) {
      console.error('[CARD-MODAL] Erro ao salvar transação:', error);
    }
  };

  // ✅ Limpar transação do localStorage (quando pagamento for concluído)
  const clearStoredTransaction = () => {
    localStorage.removeItem(getStorageKey());
    console.log('[CARD-MODAL] 🗑️ Transação removida do localStorage');
  };

  // ✅ Inicializar modal - verificar transação existente ou preparar para nova
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      return;
    }

    // Evitar múltiplas inicializações
    if (hasInitializedRef.current) {
      console.log('[CARD-MODAL] ⚠️ Já inicializado, ignorando...');
      return;
    }

    hasInitializedRef.current = true;

    // ✅ Verificar se já existe transação para este pedido
    const existingTransaction = getStoredTransaction();

    if (existingTransaction && existingTransaction.transactionId) {
      console.log('[CARD-MODAL] ♻️ Reutilizando transação existente:', existingTransaction.transactionId);
      setTransactionId(existingTransaction.transactionId);
      setPaymentStatus('processing');
      setLoading(false);
      // Iniciar polling para verificar status
      startPolling(existingTransaction.transactionId);
      toast.info(t('common:checkingPreviousPayment'));
    } else {
      // Preparar para nova transação
      fetchSessionId();
      resetForm();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isOpen, pedidoId]);

  // ✅ WebSocket para atualizações em tempo real
  // IMPORTANTE: Mantém conexão ativa mesmo quando modal é fechado
  // Só desconecta quando pagamento for concluído ou componente desmontado
  useEffect(() => {
    // Não conectar se não tiver transactionId ou se pagamento já foi concluído
    if (!transactionId || paymentStatus === 'paid' || paymentStatus === 'failed') {
      return;
    }

    if (!idEmpresa) return;

    // Se já tiver socket conectado, não criar novo
    if (socketRef.current?.connected) {
      console.log('[CARD-MODAL] ⚠️ WebSocket já conectado, ignorando...');
      return;
    }

    const API_URL = getApiUrl();

    console.log(`[CARD-MODAL] 🔌 Conectando WebSocket para transaction ${transactionId} em ${API_URL}`);

    const socket = io(API_URL, {
      withCredentials: true,
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log(`[CARD-MODAL] ✅ WebSocket conectado - Entrando na sala empresa_${idEmpresa}`);
      socket.emit('joinCompanyRoom', {
        companyId: idEmpresa.toString(),
        clientId: `card_modal_${Date.now()}`
      });
    });

    socket.on('card_payment_confirmed', (data) => {
      console.log('[CARD-MODAL] 🎉 Pagamento confirmado via WebSocket:', data);
      if (data.transaction_id === transactionId) {
        clearStoredTransaction(); // ✅ Limpar transação do localStorage
        handlePaymentSuccessInternal(data);
        // Desconectar socket após pagamento confirmado
        socket.disconnect();
        socketRef.current = null;
      }
    });

    socket.on('card_payment_failed', (data) => {
      console.log('[CARD-MODAL] ❌ Pagamento falhou via WebSocket:', data);
      if (data.transaction_id === transactionId) {
        clearStoredTransaction(); // ✅ Limpar transação do localStorage
        handlePaymentFailureInternal(data.error);
        // Desconectar socket após falha
        socket.disconnect();
        socketRef.current = null;
      }
    });

    // Cleanup: Desconectar APENAS quando componente for desmontado
    // NÃO desconectar quando modal simplesmente fechar (isOpen = false)
    return () => {
      console.log('[CARD-MODAL] 🔌 Limpando WebSocket (componente desmontado ou pagamento concluído)');
      if (socketRef.current) {
        socket.off('card_payment_confirmed');
        socket.off('card_payment_failed');
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [transactionId, paymentStatus, idEmpresa]); // ✅ Removido isOpen das dependências

  // ✅ Handlers internos que usam refs (evitar stale closures)
  const handlePaymentSuccessInternal = (data) => {
    setPaymentStatus('paid');
    toast.success(t('modal.card.paymentApprovedToast'));

    if (onPaymentSuccessRef.current) {
      onPaymentSuccessRef.current({
        transactionId: data.transaction_id || transactionId,
        pedido_id: pedidoId,
        amount,
        method: 'credit_card',
        installments: 1, // Apenas pagamento à vista
        paidAt: new Date()
      });
    }
  };

  const handlePaymentFailureInternal = (error) => {
    setPaymentStatus('failed');
    toast.error(error || t('modal.card.paymentNotApproved'));

    if (onPaymentFailedRef.current) {
      onPaymentFailedRef.current({ status: 'failed', error });
    }
  };

  const fetchSessionId = async () => {
    try {
      setLoading(true);
      setSdkError(null);

      const response = await getTunaSession(idEmpresa, customer);

      if (response.success && response.sessionId) {
        console.log('[CARD-MODAL] SessionId obtido:', response.sessionId.substring(0, 20) + '...');
        setSessionId(response.sessionId);
      } else {
        throw new Error('Falha ao obter sessionId');
      }
    } catch (error) {
      console.error('[CARD-MODAL] Erro ao obter sessionId:', error);
      setSdkError(t('common:paymentInitError'));
      toast.error(t('common:paymentSystemLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCardNumber('');
    setCardHolder('');
    setCardHolderDocument(''); // ✅ Limpar CPF/CNPJ do titular
    setCardHolderEmail(''); // ✅ Limpar email do titular
    setExpiryDate('');
    setCvv('');
    setErrors({});
    setPaymentStatus('pending');
    setTransactionId(null);
  };

  // Formatação do número do cartão
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ').substring(0, 19) : '';
  };

  // Formatação da data de validade (MM/AAAA)
  const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      // Formato MM/AAAA - permite até 4 dígitos no ano
      return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 6)}`;
    }
    return cleaned;
  };

  // ✅ Formatação de CPF/CNPJ com máscara automática
  const formatCpfCnpj = (value) => {
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.length <= 11) {
      // CPF: 000.000.000-00
      return cleaned
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    } else {
      // CNPJ: 00.000.000/0000-00
      return cleaned
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    }
  };

  // ✅ Validação de CPF
  const validateCPF = (cpf) => {
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return false;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cleaned)) return false;

    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleaned[i]) * (10 - i);
    }
    let digit = (sum * 10) % 11;
    if (digit === 10) digit = 0;
    if (digit !== parseInt(cleaned[9])) return false;

    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleaned[i]) * (11 - i);
    }
    digit = (sum * 10) % 11;
    if (digit === 10) digit = 0;
    if (digit !== parseInt(cleaned[10])) return false;

    return true;
  };

  // ✅ Validação de CNPJ
  const validateCNPJ = (cnpj) => {
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length !== 14) return false;

    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cleaned)) return false;

    // Validação do primeiro dígito verificador
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(cleaned[i]) * weights1[i];
    }
    let digit = sum % 11;
    digit = digit < 2 ? 0 : 11 - digit;
    if (digit !== parseInt(cleaned[12])) return false;

    // Validação do segundo dígito verificador
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    sum = 0;
    for (let i = 0; i < 13; i++) {
      sum += parseInt(cleaned[i]) * weights2[i];
    }
    digit = sum % 11;
    digit = digit < 2 ? 0 : 11 - digit;
    if (digit !== parseInt(cleaned[13])) return false;

    return true;
  };

  // ✅ Validação de CPF ou CNPJ
  const validateCpfCnpj = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 11) return validateCPF(cleaned);
    if (cleaned.length === 14) return validateCNPJ(cleaned);
    return false;
  };

  // ✅ Validação Luhn (algoritmo para validar números de cartão de crédito)
  const luhnCheck = (cardNum) => {
    const cleaned = cardNum.replace(/\s/g, '');

    // Precisa ter pelo menos 13 dígitos
    if (cleaned.length < 13) return false;

    let sum = 0;
    let isEven = false;

    // Percorre da direita para esquerda
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i], 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  // ✅ Validar cartão em tempo real quando o número muda
  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);

    const cleaned = formatted.replace(/\s/g, '');

    // Só validar quando tiver dígitos suficientes (13-19)
    if (cleaned.length >= 13 && cleaned.length <= 19) {
      const isValid = luhnCheck(cleaned);
      setCardValid(isValid);

      // Limpar erro se válido
      if (isValid && errors.cardNumber) {
        setErrors(prev => ({ ...prev, cardNumber: null }));
      }
    } else if (cleaned.length > 0 && cleaned.length < 13) {
      setCardValid(null); // Ainda digitando
    } else {
      setCardValid(null);
    }
  };

  // Detectar bandeira do cartão baseado no número
  const detectCardBrand = (number) => {
    const cleanNumber = number.replace(/\s/g, '');

    // Elo (deve vir antes de Visa/Mastercard por ter prefixos específicos)
    const eloRegex = /^(401178|401179|431274|438935|451416|457393|457631|457632|504175|506699|506770|506771|506772|506773|506774|506775|506776|506777|506778|509000|509001|509002|509003|509004|509005|509006|509007|509008|509009|509010|509011|509012|509013|509014|509015|509016|509017|509018|509019|509020|509021|509022|509023|509024|509025|509026|509027|509028|509029|509030|509031|509032|509033|509034|509035|509036|509037|509038|509039|509040|509041|509042|509043|509044|509045|509046|509047|509048|509049|509050|509051|509052|509053|509054|509055|509056|509057|509058|509059|509060|509061|509062|509063|509064|509065|509066|509067|509068|509069|509070|509071|509072|509073|509074|509075|509076|509077|509078|509079|509080|509081|509082|509083|509084|509085|509086|509087|509088|509089|509090|509091|509092|509093|509094|509095|509096|509097|509098|509099|636368|636297|636369|650031|650032|650033|650035|650036|650037|650038|650039|650040|650041|650042|650043|650044|650045|650046|650047|650048|650049|650050|650051|650405|650406|650407|650408|650409|650410|650411|650412|650413|650414|650415|650416|650417|650418|650419|650420|650421|650422|650423|650424|650425|650426|650427|650428|650429|650430|650431|650432|650433|650434|650435|650436|650437|650438|650439|650440|650441|650442|650443|650444|650445|650446|650447|650448|650449|650450|650451|650452|650453|650454|650455|650456|650457|650458|650459|650460|650461|650462|650463|650464|650465|650466|650467|650468|650469|650470|650471|650472|650473|650474|650475|650476|650477|650478|650479|650480|650481|650482|650483|650484|650485|650486|650487|650488|650489|650490|650491|650492|650493|650494|650495|650496|650497|650498|650499|650500|650501|650502|650503|650504|650505|650506|650507|650508|650509|650510|650511|650512|650513|650514|650515|650516|650517|650518|650519|650520|650521|650522|650523|650524|650525|650526|650527|650528|650529|650530|650531|650532|650533|650534|650535|650536|650537|650538|650539|650540|650541|650542|650543|650544|650545|650546|650547|650548|650549|650550|650551|650552|650553|650554|650555|650556|650557|650558|650559|650560|650561|650562|650563|650564|650565|650566|650567|650568|650569|650570|650571|650572|650573|650574|650575|650576|650577|650578|650579|650580|650581|650582|650583|650584|650585|650586|650587|650588|650589|650590|650591|650592|650593|650594|650595|650596|650597|650598|650599|650600|650601|650602|650603|650604|650605|650606|650607|650608|650609|650610|650611|650612|650613|650614|650615|650616|650617|650618|650619|650620|650621|650622|650623|650624|650625|650626|650627|650628|650629|650630|650631|650632|650633|650634|650635|650636|650637|650638|650639|650640|650641|650642|650643|650644|650645|650646|650647|650648|650649|650650|650651|650652|650653|650654|650655|650656|650657|650658|650659|650660|650661|650662|650663|650664|650665|650666|650667|650668|650669|650670|650671|650672|650673|650674|650675|650676|650677|650678|650679|650680|650681|650682|650683|650684|650685|650686|650687|650688|650689|650690|650691|650692|650693|650694|650695|650696|650697|650698|650699|650700|650701|650702|650703|650704|650705|650706|650707|650708|650709|650710|650711|650712|650713|650714|650715|650716|650717|650718|650719|650720|650721|650722|650723|650724|650725|650726|650727|650728|650729|650730|650731|650732|650733|650734|650735|650736|650737|650738|650739|650740|650741|650742|650743|650744|650745|650746|650747|650748|650749|650750|650751|650752|650753|650754|650755|650756|650757|650758|650759|650760|650761|650762|650763|650764|650765|650766|650767|650768|650769|650770|650771|650772|650773|650774|650775|650776|650777|650778|650779|650780|650781|650782|650783|650784|650785|650786|650787|650788|650789|650790|650791|650792|650793|650794|650795|650796|650797|650798|650799|650800|650801|650802|650803|650804|650805|650806|650807|650808|650809|650810|650811|650812|650813|650814|650815|650816|650817|650818|650819|650820|650821|650822|650823|650824|650825|650826|650827|650828|650829|650830|650831|650832|650833|650834|650835|650836|650837|650838|650839|650840|650841|650842|650843|650844|650845|650846|650847|650848|650849|650850|650851|650852|650853|650854|650855|650856|650857|650858|650859|650860|650861|650862|650863|650864|650865|650866|650867|650868|650869|650870|650871|650872|650873|650874|650875|650876|650877|650878|650879|650880|650881|650882|650883|650884|650885|650886|650887|650888|650889|650890|650891|650892|650893|650894|650895|650896|650897|650898|650899|650900|650901|650902|650903|650904|650905|650906|650907|650908|650909|650910|650911|650912|650913|650914|650915|650916|650917|650918|650919|650920|650921|650922|650923|650924|650925|650926|650927|650928|650929|650930|650931|650932|650933|650934|650935|650936|650937|650938|650939|650940|650941|650942|650943|650944|650945|650946|650947|650948|650949|650950|650951|650952|650953|650954|650955|650956|650957|650958|650959|650960|650961|650962|650963|650964|650965|650966|650967|650968|650969|650970|650971|650972|650973|650974|650975|650976|650977|650978)/;
    if (eloRegex.test(cleanNumber)) return 'elo';

    // Hipercard
    if (/^(606282|3841)/.test(cleanNumber)) return 'hipercard';

    // Amex
    if (/^3[47]/.test(cleanNumber)) return 'amex';

    // Diners
    if (/^3(?:0[0-5]|[68])/.test(cleanNumber)) return 'diners';

    // Discover
    if (/^6(?:011|5)/.test(cleanNumber)) return 'discover';

    // JCB
    if (/^(?:2131|1800|35\d{3})/.test(cleanNumber)) return 'jcb';

    // Aura
    if (/^50/.test(cleanNumber)) return 'aura';

    // Mastercard (inclui novos ranges 2221-2720)
    if (/^5[1-5]/.test(cleanNumber) || /^2(2[2-9]|[3-6]|7[0-1]|720)/.test(cleanNumber)) return 'mastercard';

    // Visa (deve vir depois de Elo pois alguns Elos começam com 4)
    if (/^4/.test(cleanNumber)) return 'visa';

    return 'other';
  };

  // Validação do formulário
  const validateForm = () => {
    const newErrors = {};

    // Número do cartão
    const cleanNumber = cardNumber.replace(/\s/g, '');
    if (!cleanNumber || cleanNumber.length < 13 || cleanNumber.length > 19) {
      newErrors.cardNumber = t('modal.card.validation.invalidCardNumber');
    }

    // Nome do titular
    if (!cardHolder || cardHolder.trim().length < 3) {
      newErrors.cardHolder = t('modal.card.validation.holderNameRequired');
    }

    // ✅ CPF/CNPJ do titular do cartão (OBRIGATÓRIO para KYC)
    const cleanDocument = cardHolderDocument.replace(/\D/g, '');
    if (!cleanDocument || (cleanDocument.length !== 11 && cleanDocument.length !== 14)) {
      newErrors.cardHolderDocument = t('modal.card.validation.documentRequired');
    } else if (!validateCpfCnpj(cleanDocument)) {
      newErrors.cardHolderDocument = cleanDocument.length === 11 ? t('modal.card.validation.invalidCpf') : t('modal.card.validation.invalidCnpj');
    }

    // ✅ Email do titular (OBRIGATÓRIO para antifraude CyberSource)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!cardHolderEmail || !cardHolderEmail.trim()) {
      newErrors.cardHolderEmail = t('modal.card.validation.emailRequired');
    } else if (!emailRegex.test(cardHolderEmail.trim())) {
      newErrors.cardHolderEmail = t('modal.card.validation.invalidEmail');
    }

    // Data de validade (formato MM/AAAA)
    const [month, year] = expiryDate.split('/');
    if (!month || !year || month.length !== 2 || year.length !== 4) {
      newErrors.expiryDate = t('modal.card.validation.invalidExpiryFormat');
    } else {
      const currentDate = new Date();
      const expDate = new Date(parseInt(year), parseInt(month) - 1); // Ano já é completo (2030)
      if (expDate < currentDate) {
        newErrors.expiryDate = t('modal.card.validation.cardExpired');
      }
    }

    // CVV
    if (!cvv || cvv.length < 3 || cvv.length > 4) {
      newErrors.cvv = t('modal.card.validation.invalidCvv');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Tokenizar cartão com Tuna SDK
  // Documentação: https://dev.tuna.uy/javascript-api/
  const tokenizeCard = async () => {
    if (!tunaInstance) {
      throw new Error('SDK Tuna não está pronto');
    }

    const [expiryMonth, expiryYear] = expiryDate.split('/');

    // ✅ DEBUG: Log detalhado dos valores de validade
    console.log('[CARD-MODAL] 🔍 DEBUG VALIDADE:', {
      expiryDateOriginal: expiryDate,
      expiryMonthRaw: expiryMonth,
      expiryYearRaw: expiryYear,
      expiryMonthFinal: parseInt(expiryMonth),
      expiryYearFinal: parseInt(expiryYear) // ✅ Enviado direto sem conversão
    });

    console.log('[CARD-MODAL] Iniciando tokenização...');

    try {
      // ✅ API correta do Tuna V2:
      // 1. Acessar o tokenizator: tuna.tokenizator()
      // 2. Usar tokenizator.generate(cardData) - retorna Promise
      const tokenizator = tunaInstance.tokenizator();

      const cardData = {
        cardNumber: cardNumber.replace(/\s/g, ''),
        cardHolderName: cardHolder,
        expirationMonth: parseInt(expiryMonth),
        expirationYear: parseInt(expiryYear), // ✅ Ano completo (ex: 2030)
        cvv: cvv,
        singleUse: true
      };

      console.log('[CARD-MODAL] Dados para tokenização:', {
        ...cardData,
        cardNumber: cardData.cardNumber.slice(0, 4) + '****' + cardData.cardNumber.slice(-4),
        cvv: '***',
        expirationMonth: cardData.expirationMonth,
        expirationYear: cardData.expirationYear
      });

      const result = await tokenizator.generate(cardData);

      console.log('[CARD-MODAL] Resultado tokenização:', result);

      if (result.code === 1 && result.token) {
        // ✅ Usar detecção local da bandeira (mais confiável que resposta do SDK)
        const detectedBrand = detectCardBrand(cardNumber);
        const finalBrand = result.brand || detectedBrand;

        console.log('[CARD-MODAL] Bandeira detectada:', { sdkBrand: result.brand, localBrand: detectedBrand, final: finalBrand });

        return {
          token: result.token,
          brand: finalBrand,
          lastFourDigits: cardNumber.replace(/\s/g, '').slice(-4)
        };
      } else {
        throw new Error(result.message || 'Falha na tokenização do cartão');
      }
    } catch (error) {
      console.error('[CARD-MODAL] Erro na tokenização:', error);
      throw error;
    }
  };

  // Processar pagamento
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!sdkReady || !tunaInstance) {
      toast.error(t('modal.card.systemNotReady'));
      return;
    }

    // 🔥 NOVO: Verificar se Device Fingerprint foi coletado
    if (!deviceFingerprintReady) {
      console.log('[CARD-MODAL] ⏳ Aguardando Device Fingerprint...');
      toast.info(t('modal.card.preparingSecurity'));
      return;
    }

    try {
      setProcessing(true);
      setPaymentStatus('processing');

      // 1. Tokenizar cartão
      console.log('[CARD-MODAL] Tokenizando cartão...');
      const tokenData = await tokenizeCard();
      console.log('[CARD-MODAL] Token obtido:', tokenData.token.substring(0, 20) + '...');

      // 2. Capturar dados do navegador para antifraude (incluindo sessionID do CyberSource)
      console.log('[CARD-MODAL] Capturando dados do navegador para antifraude...');
      const frontData = await getFrontData(antifraudSessionId); // ✅ Passa sessionID para CyberSource
      console.log('[CARD-MODAL] FrontData capturado:', {
        sessionID: frontData.sessionID ? frontData.sessionID.substring(0, 20) + '...' : 'NULL',
        ip: frontData.ipAddress,
        userAgent: frontData.userAgent?.substring(0, 50) + '...'
      });

      // 3. Enviar para backend
      console.log('[CARD-MODAL] Enviando pagamento para backend...', {
        hasTokenSession: !!sessionId,
        tokenSessionPreview: sessionId ? sessionId.substring(0, 20) + '...' : 'NULL',
        hasIP: !!frontData.ipAddress,
        hasAntifraudSessionID: !!frontData.sessionID
      });
      const paymentResponse = await createCardPaymentPublic({
        id_empresa: idEmpresa,
        amount,
        tokenSession: sessionId, // ✅ CRÍTICO: Token de sessão da Tuna
        card: {
          token: tokenData.token,
          type: 'credit_card',
          brand: tokenData.brand,
          lastFourDigits: tokenData.lastFourDigits,
          holderName: cardHolder, // ✅ Nome do titular do cartão
          holderDocument: cardHolderDocument.replace(/\D/g, ''), // ✅ CPF/CNPJ do titular (sem máscara)
          holderEmail: cardHolderEmail.trim() // ✅ Email do titular (importante para antifraude CyberSource)
        },
        installments: 1, // Apenas pagamento à vista
        customer,
        pedido_id: pedidoId,
        frontData // ✅ NOVO: Dados do navegador para antifraude (IP, userAgent, etc.)
      });

      if (paymentResponse.success) {
        console.log('[CARD-MODAL] Pagamento criado:', paymentResponse);
        setTransactionId(paymentResponse.transaction_id);

        // ✅ Salvar transação no localStorage para reutilização
        saveTransaction(paymentResponse.transaction_id, paymentResponse.status);

        // ✅ Notificar componente pai sobre a transação criada
        if (onTransactionCreated) {
          onTransactionCreated(paymentResponse.transaction_id);
        }

        // Verificar status do pagamento
        if (paymentResponse.status === 'paid' || paymentResponse.status === 'authorized') {
          handlePaymentSuccess(paymentResponse);
        } else if (paymentResponse.status === 'pending') {
          // Iniciar polling como backup
          startPolling(paymentResponse.transaction_id);
          toast.info(t('modal.card.processingPayment'));
        } else {
          handlePaymentFailure(paymentResponse.error || t('modal.card.paymentNotApproved'));
        }
      } else {
        handlePaymentFailure(paymentResponse.error || t('modal.card.paymentError'));
      }
    } catch (error) {
      console.error('[CARD-MODAL] Erro no pagamento:', error);
      handlePaymentFailure(error.message || t('modal.card.paymentError'));
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = (data) => {
    setPaymentStatus('paid');
    clearStoredTransaction(); // ✅ Limpar transação do localStorage
    toast.success(t('modal.card.paymentApprovedToast'));

    if (onPaymentSuccess) {
      onPaymentSuccess({
        transactionId: data.transaction_id || transactionId,
        pedido_id: pedidoId,
        amount,
        method: 'credit_card',
        installments: 1, // Apenas pagamento à vista
        paidAt: new Date()
      });
    }

    // Fechar modal após 2 segundos
    setTimeout(() => onClose(), 2000);
  };

  const handlePaymentFailure = (error, allowRetry = true) => {
    setPaymentStatus('failed');
    clearStoredTransaction(); // ✅ Limpar transação do localStorage

    // ✅ Mostrar mensagem de erro amigável
    const errorMessage = error || t('modal.card.cardNotAuthorized');
    toast.error(errorMessage, {
      autoClose: 5000, // Manter toast visível por mais tempo
    });

    if (onPaymentFailed) {
      onPaymentFailed({ status: 'failed', error: errorMessage });
    }

    // ✅ Se permitir retry, resetar formulário após 3 segundos para permitir nova tentativa
    if (allowRetry) {
      setTimeout(() => {
        setPaymentStatus('pending');
        setProcessing(false);
        // Não limpar formulário para facilitar correção de dados
      }, 3000);
    }
  };

  const startPolling = useCallback((txId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await getPaymentStatusPublic(txId);

        if (response.success && response.status !== 'pending') {
          clearInterval(pollingIntervalRef.current);

          if (response.status === 'paid' || response.status === 'authorized') {
            handlePaymentSuccess({ transaction_id: txId });
          } else {
            handlePaymentFailure(t('modal.card.paymentNotApproved'));
          }
        }
      } catch (error) {
        console.error('[CARD-MODAL] Erro no polling:', error);
      }
    }, 3000);
  }, []);

  const handleClose = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <FaTimes />
        </CloseButton>

        <ModalContent>
          {loading || sdkLoading ? (
            <LoadingSpinner>
              <FaSpinner />
              <span>{t('modal.card.loadingPaymentSystem')}</span>
            </LoadingSpinner>
          ) : sdkError || sdkLoadError ? (
            <StatusOverlay>
              <FaTimesCircle style={{ color: '#f44336' }} />
              <h3>{t('modal.card.loadError')}</h3>
              <p>{sdkError || sdkLoadError}</p>
            </StatusOverlay>
          ) : paymentStatus === 'paid' ? (
            <StatusOverlay>
              <FaCheckCircle style={{ color: '#4caf50' }} />
              <h3>{t('modal.card.approved')}</h3>
              <p>{t('modal.card.approvedMsg')}</p>
            </StatusOverlay>
          ) : paymentStatus === 'failed' ? (
            <StatusOverlay>
              <FaTimesCircle style={{ color: '#f44336' }} />
              <h3>{t('modal.card.declined')}</h3>
              <p>{t('modal.card.declinedMsg')}</p>
              <SubmitButton
                type="button"
                onClick={() => {
                  setPaymentStatus('pending');
                  setProcessing(false);
                }}
                style={{ marginTop: '20px', maxWidth: '200px' }}
              >
                {t('modal.card.tryAgain')}
              </SubmitButton>
            </StatusOverlay>
          ) : (
            <>
              <CardIcon>
                <FaCreditCard />
              </CardIcon>

              <Title>{t('modal.card.title')}</Title>
              <Subtitle>{t('modal.card.subtitle')}</Subtitle>

              <PaymentAmount>
                <span>{t('modal.card.amount')}</span>
                <strong>{formatPriceWithSymbol(amount)}</strong>
              </PaymentAmount>

              <Form data-support-private onSubmit={handleSubmit}>
                <InputGroup>
                  <label>{t('modal.card.cardNumber')}</label>
                  <InputWrapper>
                    <Input
                      type="text"
                      value={cardNumber}
                      onChange={handleCardNumberChange}
                      placeholder="0000 0000 0000 0000"
                      maxLength={19}
                      disabled={processing}
                      $error={errors.cardNumber || cardValid === false}
                      style={{ paddingRight: cardValid !== null ? '40px' : '16px' }}
                    />
                    {cardValid !== null && (
                      <ValidationIcon className={cardValid ? 'valid' : 'invalid'}>
                        {cardValid ? <FaCheckCircle /> : <FaTimesCircle />}
                      </ValidationIcon>
                    )}
                  </InputWrapper>
                  {cardValid === false && <ErrorText>{t('modal.card.invalidCardNumber')}</ErrorText>}
                  {errors.cardNumber && cardValid !== false && <ErrorText>{errors.cardNumber}</ErrorText>}
                </InputGroup>

                <InputGroup>
                  <label>{t('modal.card.holderName')}</label>
                  <Input
                    type="text"
                    value={cardHolder}
                    onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                    placeholder={t('modal.card.holderNamePlaceholder')}
                    disabled={processing}
                    $error={errors.cardHolder}
                  />
                  {errors.cardHolder && <ErrorText>{errors.cardHolder}</ErrorText>}
                </InputGroup>

                <InputGroup>
                  <label>{t('modal.card.holderDocument')}</label>
                  <Input
                    type="text"
                    value={cardHolderDocument}
                    onChange={(e) => setCardHolderDocument(formatCpfCnpj(e.target.value))}
                    placeholder={t('modal.card.holderDocumentPlaceholder')}
                    maxLength={18}
                    disabled={processing}
                    $error={errors.cardHolderDocument}
                  />
                  {errors.cardHolderDocument && <ErrorText>{errors.cardHolderDocument}</ErrorText>}
                </InputGroup>

                <InputGroup>
                  <label>{t('modal.card.holderEmail')}</label>
                  <Input
                    type="email"
                    value={cardHolderEmail}
                    onChange={(e) => setCardHolderEmail(e.target.value.toLowerCase())}
                    placeholder={t('modal.card.holderEmailPlaceholder')}
                    disabled={processing}
                    $error={errors.cardHolderEmail}
                  />
                  {errors.cardHolderEmail && <ErrorText>{errors.cardHolderEmail}</ErrorText>}
                </InputGroup>

                <Row>
                  <InputGroup>
                    <label>{t('modal.card.expiry')}</label>
                    <Input
                      type="text"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                      placeholder={t('modal.card.expiryPlaceholder')}
                      maxLength={7}
                      disabled={processing}
                      $error={errors.expiryDate}
                    />
                    {errors.expiryDate && <ErrorText>{errors.expiryDate}</ErrorText>}
                  </InputGroup>

                  <InputGroup>
                    <label>{t('modal.card.cvv')}</label>
                    <Input
                      type="text"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 4))}
                      placeholder={t('modal.card.cvvPlaceholder')}
                      maxLength={4}
                      disabled={processing}
                      $error={errors.cvv}
                    />
                    {errors.cvv && <ErrorText>{errors.cvv}</ErrorText>}
                  </InputGroup>
                </Row>

                <SubmitButton type="submit" disabled={processing || !sdkReady} style={{height:50}}>
                  {processing ? (
                    <>
                      <FaSpinner className="spin" /> {t('modal.card.processing')}
                    </>
                  ) : (
                    <>
                      <FaLock /> {t('modal.card.pay')} {formatPriceWithSymbol(amount)}
                    </>
                  )}
                </SubmitButton>

                <SecurityBadge>
                  <FaLock />
                  <span>{t('modal.card.securityBadge')}</span>
                </SecurityBadge>
              </Form>
            </>
          )}
        </ModalContent>
      </Modal>
    </Overlay>
  );
};

export default ModalPaymentCard;
